import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import Utils from "Utils/utils";
import {
  BULK_TASK_TYPE,
  UPDATE_TASK_READ_STATUS_LABEL
} from "Model/model";
import bulkJobsNotificationStore, {
  BULK_NOTIFICATION_OPEN_STATUSES
} from "Stores/bulkJobsNotification";

const STATUS_LABELS = {
  TO_BE_PROCESSED: "Pending",
  IN_PROGRESS: "In Progress",
  PROCESSED: "Completed",
  FAILED: "Failed"
};

const CLOSED_POLL_INTERVAL = 30000;
const OPEN_POLL_INTERVAL = 10000;

const getJobLabel = (job = {}) => {
  if (job?.type === "UPDATE_TASK_READ_STATUS") {
    return (
      UPDATE_TASK_READ_STATUS_LABEL[job?.readUnreadJobType || ""] ||
      "Update Read Status"
    );
  }
  return BULK_TASK_TYPE[job?.type] || job?.type || "Bulk Action";
};

const getProgressLabel = (job = {}) => {
  const {
    totalContacts = 0,
    processedContacts = 0,
    totalCount = 0,
    successCount = 0,
    failedCount = 0
  } = job || {};

  if (totalContacts > 0 && BULK_NOTIFICATION_OPEN_STATUSES.includes(job?.status)) {
    return `${processedContacts}/${totalContacts} processed`;
  }
  if (totalCount > 0) {
    const completedCount = successCount + failedCount;
    return `${completedCount}/${totalCount} processed`;
  }
  return "";
};

const formatJobDate = createTs => {
  if (!createTs) {
    return "--";
  }
  try {
    return new Date(createTs).toLocaleString([], {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  } catch (error) {
    return Utils.getDateFromString(createTs);
  }
};

const BulkJobNotification = observer(() => {
  const history = useHistory();
  const wrapperRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    jobs = [],
    openJobsCount = 0,
    guideVisible = false,
    highlightActive = false,
    guideMessage = ""
  } = bulkJobsNotificationStore;

  const closeDropdown = () => {
    setShowDropdown(false);
  };

  const handleBellClick = () => {
    bulkJobsNotificationStore.dismissGuide();
    const nextValue = !showDropdown;
    setShowDropdown(nextValue);
    if (nextValue) {
      bulkJobsNotificationStore.fetchJobs();
    }
  };

  const handleViewAll = () => {
    closeDropdown();
    history.push("/settings/bulk-actions");
  };

  useEffect(() => {
    bulkJobsNotificationStore.fetchJobs();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        bulkJobsNotificationStore.fetchJobs();
      }
    }, showDropdown ? OPEN_POLL_INTERVAL : CLOSED_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [showDropdown]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div
      className={`bulkJobsNotificationWrapper ${
        highlightActive ? "highlightActive" : ""
      }`}
      ref={wrapperRef}
    >
      <button
        type="button"
        className={`bulkJobsNotificationButton ${
          showDropdown ? "dropdownOpen" : ""
        }`}
        title="Bulk job notifications"
        onClick={handleBellClick}
      >
        <i className="material-icons-outlined">notifications_none</i>
        {openJobsCount > 0 && (
          <span className="bulkJobsNotificationBadge">{openJobsCount}</span>
        )}
      </button>
      {guideVisible && (
        <div className="bulkJobsGuideTooltip">
          <span className="guideDot" />
          <span>{guideMessage}</span>
        </div>
      )}
      {showDropdown && (
        <div className="bulkJobsDropdown">
          <div className="bulkJobsDropdownHeader">
            <div>
              <div className="bulkJobsDropdownTitle">Bulk Actions</div>
              <div className="bulkJobsDropdownSubtitle">
                {openJobsCount > 0
                  ? `${openJobsCount} open job${openJobsCount > 1 ? "s" : ""}`
                  : "No open jobs"}
              </div>
            </div>
          </div>
          <div className="bulkJobsDropdownBody">
            {jobs?.length > 0 ? (
              jobs.map(job => {
                const progressLabel = getProgressLabel(job);
                return (
                  <div className="bulkJobItem" key={job?.id}>
                    <div className="bulkJobItemTopRow">
                      <div className="bulkJobItemTitle">{getJobLabel(job)}</div>
                      <span
                        className={`bulkJobStatus ${
                          String(job?.status || "").toLowerCase()
                        }`}
                      >
                        {STATUS_LABELS[job?.status] || "--"}
                      </span>
                    </div>
                    <div className="bulkJobItemBottomRow">
                      <span className="bulkJobCreatedAt">
                        {formatJobDate(job?.createTs)}
                      </span>
                      {progressLabel && (
                        <span className="bulkJobProgress">{progressLabel}</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bulkJobsEmptyState">
                No bulk actions found yet.
              </div>
            )}
          </div>
          <button
            type="button"
            className="bulkJobsViewAllBtn"
            onClick={handleViewAll}
          >
            View All
          </button>
        </div>
      )}
    </div>
  );
});

export default BulkJobNotification;
